/**
 * Utility functions for the application
 * All utility functions should be placed here to avoid scattered utility files
 */

/*
|--------------------------------------------------------------------------
| DATE HELPERS
|--------------------------------------------------------------------------
*/

const DATE_LOCALES: Record<string, string> = {
  en: 'en-US',
};

/**
 * Get the locale string for date formatting
 * @param lang - Language code
 * @returns Locale string for Intl APIs
 */
export const getDateLocale = (lang: string): string => {
  return DATE_LOCALES[lang] || lang;
};

/**
 * Convert a date string to UTC Date object
 * @param dateStr - Date string to convert
 * @returns UTC Date object
 */
export const utc = (dateStr: string): Date => {
  return new Date(new Date(dateStr).toUTCString().substring(0, 25));
};

/**
 * Format a Date object or date string to YYYY-MM-DD using UTC methods.
 * Avoids timezone issues by using getUTCFullYear, getUTCMonth, getUTCDate.
 * @param date - Date object or ISO date string (e.g., '2026-02-19' or '2026-02-19T00:00:00.000Z')
 * @returns Date string in YYYY-MM-DD format
 * @example
 * ```ts
 * formatToUTCDate(new Date('2026-02-19')) // '2026-02-19'
 * formatToUTCDate('2026-02-19T00:00:00.000Z') // '2026-02-19'
 * ```
 */
export const formatToUTCDate = (date: Date | string): string => {
  if (typeof date === 'string') {
    // Already a string - extract just the date part (before T) if it has time component
    return date.split('T')[0];
  }
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Normalize a date string into YYYY-MM-DD when possible.
 * If the input is already in YYYY-MM-DD format, it is returned unchanged.
 * @param dateString - Input date string
 * @returns Simple date string when convertible; otherwise the original input
 */
export const formatToSimpleDate = (dateString: string): string => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }

  const date = new Date(dateString);
  if (!Number.isNaN(date.getTime())) {
    return date.toISOString().split('T')[0];
  }

  return dateString;
};

/**
 * Mapping of locales to their IANA timezone identifiers.
 * Used for converting studio local times to UTC.
 */
const LOCALE_TIMEZONES: Record<string, string> = {
  'en-us': 'America/Los_Angeles', // PST/PDT
  'en-ca': 'America/Toronto', // EST/EDT
  'fr-ca': 'America/Toronto', // EST/EDT
  'de-de': 'Europe/Berlin', // CET/CEST
  'fr-fr': 'Europe/Paris', // CET/CEST
  'nl-nl': 'Europe/Amsterdam', // CET/CEST
  'nb-no': 'Europe/Oslo', // CET/CEST
  'de-ch': 'Europe/Zurich', // CET/CEST
  'fr-ch': 'Europe/Zurich', // CET/CEST
  'en-be': 'Europe/Brussels', // CET/CEST
  'nl-be': 'Europe/Brussels', // CET/CEST
  'fr-be': 'Europe/Brussels', // CET/CEST
  'de-be': 'Europe/Brussels', // CET/CEST
  'da-dk': 'Europe/Copenhagen', // CET/CEST
  'en-sa': 'Asia/Riyadh', // AST (UTC+3)
  'ar-sa': 'Asia/Riyadh', // AST (UTC+3)
  'en-ae': 'Asia/Dubai', // GST (UTC+4)
  'ar-ae': 'Asia/Dubai', // GST (UTC+4)
};

/**
 * Get the IANA timezone for a locale
 * @param locale - Locale string (e.g., 'de-de', 'en-us')
 * @returns IANA timezone identifier
 */
export const getTimezoneForLocale = (locale: string): string => {
  if (!locale) return 'Europe/Berlin';
  return LOCALE_TIMEZONES[locale.toLowerCase()] || 'America/Los_Angeles';
};

/**
 * Combines a date string with hours and minutes into an OffsetDateTime string.
 * The time is interpreted as the studio's local time (based on locale) and converted to UTC.
 * @param dateStr - Date in YYYY-MM-DD format
 * @param hours - Hour (0-23) in studio local time
 * @param minutes - Minutes (0-59)
 * @param locale - Locale to determine studio timezone (e.g., 'de-de' → Europe/Berlin)
 * @returns OffsetDateTime string in UTC (e.g., '2026-02-12T11:00:00+00:00')
 * @example
 * ```ts
 * // German studio (CET = UTC+1) at 12:00 local time
 * localToUTC('2026-02-12', 12, 0, 'de-de') // '2026-02-12T11:00:00+00:00'
 *
 * // US studio (PST = UTC-8) at 12:00 local time
 * localToUTC('2026-02-12', 12, 0, 'en-us') // '2026-02-12T20:00:00+00:00'
 * ```
 */
export const zonedToUTC = (
  dateStr: string,
  hours: number,
  minutes: number,
  timezone: string
): string => {
  // Use Intl.DateTimeFormat to get the UTC offset for this timezone at this date/time
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  // Create a "guess" date in UTC, then adjust to find the correct UTC time
  const [year, month, day] = dateStr.split('-').map(Number);

  // Start with the local time as if it were UTC
  const utcGuess = Date.UTC(year, month - 1, day, hours, minutes, 0, 0);

  // Format this UTC time in the target timezone to see what local time it shows
  const parts = formatter.formatToParts(new Date(utcGuess));
  const getPart = (type: string) => parseInt(parts.find((p) => p.type === type)?.value || '0', 10);

  const guessLocalHour = getPart('hour');
  const guessLocalMinute = getPart('minute');
  const guessLocalDay = getPart('day');

  // Calculate the difference between the target local time and what we got
  let hourDiff = hours - guessLocalHour;
  let dayDiff = day - guessLocalDay;

  // Handle day boundary (e.g., if we wanted 2:00 AM but got 11:00 PM previous day)
  if (dayDiff > 15) dayDiff = dayDiff - 31; // End of month wrap
  if (dayDiff < -15) dayDiff = dayDiff + 31; // Start of month wrap

  hourDiff += dayDiff * 24;
  const minuteDiff = minutes - guessLocalMinute;

  // Adjust our UTC guess by the difference
  const offsetMs = (hourDiff * 60 + minuteDiff) * 60 * 1000;
  const utcDate = new Date(utcGuess + offsetMs);

  // Convert to OffsetDateTime format
  return utcDate.toISOString().replace(/\.\d{3}Z$/, '+00:00');
};

export const localToUTC = (
  dateStr: string,
  hours: number,
  minutes: number,
  locale = 'en-us'
): string => zonedToUTC(dateStr, hours, minutes, getTimezoneForLocale(locale));

/** `2026-09-09T17:00:00+00:00` -> `2026-09-09T17:00:00Z`. */
export const toZuluDatetime = (datetime?: string | null): string =>
  datetime ? datetime.replace(/\+00:00$/, 'Z') : '';

/**
 * Format a Date object as OffsetDateTime string (ISO 8601 format without milliseconds).
 * Useful when the backend expects OffsetDateTime format.
 * @param date - The date to format
 * @returns Formatted date string in the format: YYYY-MM-DDTHH:mm:ss+00:00
 * @example
 * ```ts
 * formatToOffsetDateTime(new Date()) // '2025-12-19T15:23:19+00:00'
 * ```
 */
export const formatToOffsetDateTime = (date: Date): string => {
  return date.toISOString().replace(/\.\d{3}Z$/, '+00:00');
};

/**
 * Extract hours and minutes from a UTC datetime string, converted to local studio time.
 * This is the inverse of localToUTC - converts UTC back to studio local time for display.
 * @param datetime - ISO datetime string in UTC (e.g., '2026-02-12T11:00:00+00:00')
 * @param locale - Locale to determine studio timezone (e.g., 'de-de' → Europe/Berlin)
 * @returns Object with hour and minute in studio local time
 * @example
 * ```ts
 * // 11:00 UTC displayed for German studio (CET = UTC+1)
 * getLocalTimeFromUTC('2026-02-12T11:00:00+00:00', 'de-de') // { hour: 12, minute: 0 }
 *
 * // 20:00 UTC displayed for US studio (PST = UTC-8)
 * getLocalTimeFromUTC('2026-02-12T20:00:00+00:00', 'en-us') // { hour: 12, minute: 0 }
 * ```
 */
export const getZonedTimeFromUTC = (
  datetime: string | undefined | null,
  timezone: string
): { hour: number; minute: number } | null => {
  if (!datetime) return null;

  try {
    const date = new Date(datetime);
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    const parts = formatter.formatToParts(date);
    const hour = parseInt(parts.find((p) => p.type === 'hour')?.value || '0', 10);
    const minute = parseInt(parts.find((p) => p.type === 'minute')?.value || '0', 10);

    return { hour, minute };
  } catch {
    return null;
  }
};

/**
 * Format a UTC range as a 24-hour `H:mm-H:mm` slot in the given timezone.
 * Matches the value shape of the slots returned by the availability API.
 */
export const utcToZonedTimeSlot = (
  startDatetime: string | undefined | null,
  endDatetime: string | undefined | null,
  timezone: string
): string => {
  const start = getZonedTimeFromUTC(startDatetime, timezone);
  const end = getZonedTimeFromUTC(endDatetime, timezone);
  if (!start || !end) return '';

  const format = ({ hour, minute }: { hour: number; minute: number }) =>
    `${hour}:${minute.toString().padStart(2, '0')}`;

  return `${format(start)}-${format(end)}`;
};

/**
 * Start of a `H:mm-H:mm` slot in minutes, for ordering slots chronologically.
 * The hour is not zero padded, so slots cannot be compared as strings.
 */
export const timeSlotStartMinutes = (slot: string): number => {
  const [hour, minute] = slot.split('-')[0].split(':').map(Number);
  return (hour || 0) * 60 + (minute || 0);
};

export const getLocalTimeFromUTC = (
  datetime: string | undefined | null,
  locale = 'en-us'
): { hour: number; minute: number } | null => {
  if (!datetime) return null;

  try {
    const timezone = getTimezoneForLocale(locale);
    const date = new Date(datetime);

    // Use Intl.DateTimeFormat to get the time in the target timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    const parts = formatter.formatToParts(date);
    const hour = parseInt(parts.find((p) => p.type === 'hour')?.value || '0', 10);
    const minute = parseInt(parts.find((p) => p.type === 'minute')?.value || '0', 10);

    return { hour, minute };
  } catch {
    return null;
  }
};

/**
 * Format a time slot range from UTC datetime strings to studio local time.
 * Converts UTC times back to the studio's local timezone for display.
 * @param startDatetime - ISO datetime string in UTC for start
 * @param endDatetime - ISO datetime string in UTC for end
 * @param locale - Locale to determine studio timezone
 * @returns Time range string matching dropdown options (e.g., "12:00-12:30")
 * @example
 * ```ts
 * // 11:00-11:30 UTC displayed for German studio (CET = UTC+1)
 * utcToLocalTimeSlot('2026-02-12T11:00:00+00:00', '2026-02-12T11:30:00+00:00', 'de-de')
 * // Returns "12:00-12:30"
 * ```
 */
export const utcToLocalTimeSlot = (
  startDatetime?: string,
  endDatetime?: string,
  locale = 'en-us'
): string => {
  if (!startDatetime || !endDatetime) return '';

  const start = getLocalTimeFromUTC(startDatetime, locale);
  const end = getLocalTimeFromUTC(endDatetime, locale);

  if (!start || !end) return '';

  const formatTime = (hour: number, minute: number): string => {
    const h = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    const m = minute.toString().padStart(2, '0');
    return `${h}:${m}`;
  };

  return `${formatTime(start.hour, start.minute)}-${formatTime(end.hour, end.minute)}`;
};

/**
 * Extract date part from datetime string for date picker components.
 * @param datetime - ISO datetime string or any valid date string
 * @returns Date string in format YYYY-MM-DD or empty string if invalid
 * @example
 * ```ts
 * formatDateForPicker('2024-12-19T15:23:19+00:00') // '2024-12-19'
 * formatDateForPicker(null) // ''
 * ```
 */
export const formatDateForPicker = (datetime?: string | null): string => {
  if (!datetime) return '';
  try {
    const date = new Date(datetime);
    return date.toISOString().split('T')[0];
  } catch {
    return '';
  }
};

/**
 * Extract date part from UTC datetime string, converted to studio local date.
 * This handles the case where UTC date differs from local date (e.g., 6 PM PST = 2 AM UTC next day).
 * @param datetime - ISO datetime string in UTC (e.g., '2026-02-13T02:00:00+00:00')
 * @param locale - Locale to determine studio timezone (e.g., 'en-us' → America/Los_Angeles)
 * @returns Date string in format YYYY-MM-DD in studio local timezone
 * @example
 * ```ts
 * // 6 PM PST on Feb 12 stored as 2 AM UTC on Feb 13
 * utcToLocalDate('2026-02-13T02:00:00+00:00', 'en-us') // '2026-02-12'
 *
 * // Noon CET on Feb 12 stored as 11 AM UTC on Feb 12
 * utcToLocalDate('2026-02-12T11:00:00+00:00', 'de-de') // '2026-02-12'
 * ```
 */
export const utcToLocalDate = (datetime?: string | null, locale = 'en-us'): string => {
  if (!datetime) return '';

  try {
    const timezone = getTimezoneForLocale(locale);
    const date = new Date(datetime);

    // Use Intl.DateTimeFormat to get the date in the target timezone
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });

    // en-CA locale formats as YYYY-MM-DD which is what we need
    return formatter.format(date);
  } catch {
    return '';
  }
};

/**
 * Format a date string using Date#toLocaleString.
 * Use `language` to control the display language independently from the locale
 * (e.g., render English month names while keeping locale-specific formatting rules).
 * @param dateStr - The date string to format
 * @param locale - Locale for formatting rules (default: 'en-US')
 * @param options - Intl.DateTimeFormatOptions (note: when `timeStyle` is provided, default date parts are omitted)
 * @param language - Display language override (e.g., 'en' to render in English regardless of locale)
 * @returns Formatted date string or '-' if invalid
 * @example
 * ```ts
 * formatDate('2024-01-15') // "Jan 15, 2024"
 * formatDate('2024-01-15', 'de-DE') // "15. Jan. 2024"
 * formatDate('2024-01-15', 'de-DE', {}, 'en') // "Jan 15, 2024" (English display, DE formatting)
 * formatDate(null) // "-"
 * ```
 */
export const formatDate = (
  dateStr: string | null | undefined,
  locale = 'en-US',
  options: Intl.DateTimeFormatOptions = {},
  language?: string
): string => {
  if (!dateStr) return '-';

  const date = utc(dateStr);
  if (isNaN(date.getTime())) return '-';

  // w20-13562 – force en-sa to use en-us
  const resolvedLocale = locale.toLowerCase() === 'en-sa' ? 'en-US' : locale;

  // Use language for display (month names etc.), fall back to locale
  const displayLocale = language || getDateLocale(resolvedLocale);

  // timeStyle cannot be combined with individual date/time options per toLocaleString docs
  const formatOptions: Intl.DateTimeFormatOptions =
    'timeStyle' in options
      ? options
      : {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          ...options,
        };

  return date.toLocaleString(displayLocale, formatOptions);
};

/**
 * Format a date for the active label language: NL as "29 sep 2026", all others as "Sep 29, 2026".
 * @param dateStr - The date string to format
 * @param contentfulLocale - Active language locale, e.g. 'nl-NL' or 'en-US'
 * @returns Formatted date string or '-' if empty/invalid
 */
export const formatDateForLanguage = (
  dateStr: string | null | undefined,
  contentfulLocale?: string
): string => {
  if (!dateStr) return '-';
  if (!contentfulLocale?.toLowerCase().startsWith('nl')) return formatDate(dateStr);

  const date = utc(dateStr);
  if (isNaN(date.getTime())) return '-';

  const day = String(date.getDate()).padStart(2, '0');
  // Month-only formatting yields the undotted short form ("sep")
  const month = date.toLocaleDateString('nl-NL', { month: 'short' }).toLowerCase();
  return `${day} ${month} ${date.getFullYear()}`;
};

/**
 * Format a date string with ordered format (day month year)
 * Used for specific locales like en-sa that need "07 December 2023" format
 * @param dateStr - The date string to format
 * @param locale - Locale for formatting
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string
 */
export const orderedFormat = (
  dateStr: string,
  locale: string,
  options: Intl.DateTimeFormatOptions = {}
): string => {
  const date = new Date(utc(dateStr));
  if (locale === 'en-sa') {
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', options);
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  } else {
    return formatDate(dateStr, locale, options);
  }
};

/**
 * Normalizes truthy flags that may come from API payloads as booleans or strings.
 * Accepts `true` and case-insensitive `'true'`; all other values are false.
 * @param value - Unknown flag value to evaluate
 * @returns Whether the value should be treated as true
 */
export const isTruthyFlag = (value: unknown): boolean => {
  if (value === true) {
    return true;
  }

  if (typeof value === 'string') {
    return value.trim().toLowerCase() === 'true';
  }

  return false;
};

/**
 * Type guard for Object.entries values that are not null/undefined,
 * with optional key exclusions.
 *
 * @param entry - Key-value tuple from Object.entries
 * @param excludedKeys - Optional set of keys to exclude
 * @returns Whether the entry value is non-nullish and key is not excluded
 */
export const isNonNullableEntry = <TValue>(
  entry: [string, TValue | null | undefined],
  excludedKeys?: ReadonlySet<string>
): entry is [string, TValue] => {
  const [key, value] = entry;
  return value !== null && value !== undefined && !excludedKeys?.has(key);
};

/**
 * Format a date string to a human-readable date and time format.
 * Use `language` to control the display language independently from the locale
 * (e.g., render English month names while keeping 24-hour format from a non-US locale).
 * @param dateString - The date string to format (ISO format or any valid date string)
 * @param locale - Locale for formatting rules like 24h clock (default: 'en-US')
 * @param language - Display language override (e.g., 'en' to render in English regardless of locale)
 * @returns Formatted date string like "Dec 18, 2024, 03:30 PM" or '-' if invalid
 * @example
 * ```ts
 * formatDateTime('2024-12-18T15:30:00Z') // "Dec 18, 2024, 03:30 PM"
 * formatDateTime('2024-12-18T15:30:00Z', 'de-DE') // "18. Dez. 2024, 15:30"
 * formatDateTime('2024-12-18T15:30:00Z', 'de-DE', 'en') // "Dec 18, 2024, 15:30"
 * formatDateTime(null) // "-"
 * ```
 */
export const formatDateTime = (
  dateString: string | null | undefined,
  locale = 'en-US',
  language?: string,
  timezone?: string
): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';

  // Use 24-hour format for non-US locales
  const use24Hour = !locale.toLowerCase().startsWith('en-us');

  // Use language for display (month names etc.), fall back to locale
  const displayLocale = language || getDateLocale(locale);

  return date.toLocaleString(displayLocale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: !use24Hour,
    ...(timezone ? { timeZone: timezone } : {}),
  });
};

/**
 * Formats an appointment as its start date and time plus the end time, e.g. "29 Sept 2026, 10:00 - 11:00".
 * Falls back to the start alone when the end is missing or invalid.
 */
export const formatDateTimeRange = (
  startString: string | null | undefined,
  endString: string | null | undefined,
  locale = 'en-US',
  language?: string,
  timezone?: string
): string => {
  const start = formatDateTime(startString, locale, language, timezone);
  const end = endString ? new Date(endString) : null;
  if (start === '-' || !end || isNaN(end.getTime())) return start;

  const endTime = end.toLocaleTimeString(language || getDateLocale(locale), {
    hour: '2-digit',
    minute: '2-digit',
    // Matches formatDateTime: 12-hour for en-US only.
    hour12: locale.toLowerCase().startsWith('en-us'),
    ...(timezone ? { timeZone: timezone } : {}),
  });

  return `${start} - ${endTime}`;
};

/**
 * Formats a date as `DD-MMM-YYYY HH:MM`, e.g. "12-Oct-2026 09:00 AM" (en-US) or "12-Okt-2026 09:00" (de-DE).
 * Same 12/24-hour rule as formatDateTime.
 */
export const formatDayMonthYearTime = (
  dateString: string | null | undefined,
  locale = 'en-US',
  timezone?: string
): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';

  const parts = new Intl.DateTimeFormat(getDateLocale(locale), {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: locale.toLowerCase().startsWith('en-us'),
    ...(timezone ? { timeZone: timezone } : {}),
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  const dayPeriod = part('dayPeriod');

  return `${part('day')}-${part('month').replace('.', '')}-${part('year')} ${part('hour')}:${part('minute')}${dayPeriod ? ` ${dayPeriod}` : ''}`;
};

/**
 * Calculate the number of days between a date and now
 * @param start - Start date string
 * @returns Number of days (negative if in the past, positive if in the future)
 * @example
 * ```ts
 * getDaysUntilNow('2024-12-25') // days until Christmas
 * ```
 */
export const getDaysUntilNow = (start: string): number => {
  const MS_PER_DAY = 1000 * 60 * 60 * 24;
  const then = new Date(start).setHours(0, 0, 0, 0);
  const now = new Date().setHours(0, 0, 0, 0);
  const daysDifference = (then - now) / MS_PER_DAY;
  return daysDifference;
};

/*
|--------------------------------------------------------------------------
| PRICING LABEL HELPERS
|--------------------------------------------------------------------------
*/

type PricingLabels = Record<string, string> | undefined;

/** Resolves the localized VAT label, falling back to English. */
export const resolveVatLabel = (labels: PricingLabels): string =>
  labels?.['vehicle.vat'] ?? labels?.['invoice.summary.totalTax'] ?? 'VAT';

/** Resolves known NL statutory fee aliases to localized labels, preserving unknown fee names. */
export const resolveNlFeeLabel = (feeLabel: string, labels: PricingLabels): string => {
  const normalizedLabel = feeLabel.toLowerCase().replace(/[^a-z0-9]/g, '');

  if (normalizedLabel.includes('recycl') || normalizedLabel === 'rcylfee') {
    return labels?.['vehicle.recyclingFee'] ?? 'Recycling Management Fee';
  }
  if (normalizedLabel.includes('bpm')) {
    return labels?.['vehicle.bpmTax'] ?? 'BPM';
  }
  if (
    normalizedLabel.includes('registration') ||
    normalizedLabel.includes('registratie') ||
    normalizedLabel === 'regcost'
  ) {
    return labels?.['vehicle.registrationFee'] ?? 'Registration Fee';
  }

  return feeLabel;
};

/*
|--------------------------------------------------------------------------
| LOCALE HELPERS
|--------------------------------------------------------------------------
*/

/**
 * Converts URL-friendly locale format (lowercase) to Contentful format
 * @param locale - Locale in format like 'en-us', 'de-de', 'fr-ca'
 * @returns Locale in Contentful format like 'en-US', 'de-DE', 'fr-CA'
 * @example
 * ```ts
 * convertToContentfulLocale('en-us') // 'en-US'
 * convertToContentfulLocale('de-de') // 'de-DE'
 * ```
 */
export const convertToContentfulLocale = (locale: string): string => {
  if (!locale || !locale.includes('-')) return 'de-DE';
  const [language, country] = locale.split('-');
  return `${language}-${country.toUpperCase()}`;
};

/**
 * Converts a label-language override to its Contentful locale
 * @param languageCode - Language code like 'en', 'de', 'fr'
 * @param countryCode - Optional country code (e.g., 'NL', 'US') for locale-specific handling
 * @returns Locale in Contentful format like 'en-US', 'de-DE', 'en-NL'
 * @example
 * ```ts
 * getContentfulLocaleFromLanguage('en') // 'en-US'
 * getContentfulLocaleFromLanguage('en', 'NL') // 'en-NL'
 * getContentfulLocaleFromLanguage('de') // 'de-DE'
 * ```
 */
export const getContentfulLocaleFromLanguage = (
  languageCode: string,
  countryCode?: string
): string => {
  // Special handling for NL (Netherlands) users with English language
  if (languageCode.toLowerCase() === 'en' && countryCode?.toUpperCase() === 'NL') {
    return 'en-NL';
  }

  const languageToLocale: Record<string, string> = {
    en: 'en-US',
    de: 'de-DE',
    fr: 'fr-FR',
    nl: 'nl-NL',
    nb: 'nb-NO',
    ar: 'ar-SA',
  };
  return languageToLocale[languageCode.toLowerCase()] || 'en-US';
};

/**
 * Resolves the Contentful locale for the active label language.
 * English falls back to the user's region: NL (non-German locale) → 'en-NL', otherwise 'en-US'.
 * Any other (non-English) language override falls back to the user's regional locale as-is.
 * @param language - Label-language override from the store ('en' or null)
 * @param countryCode - User's country code (e.g., 'NL', 'US')
 * @param locale - User's regional locale (e.g., 'en-us', 'de-de')
 */
export const resolveEffectiveLocale = (
  language: string | null,
  countryCode: string | undefined,
  locale: string
): string => {
  if (language !== 'en') {
    return convertToContentfulLocale(locale);
  }
  const isNlWithNonGermanLocale =
    countryCode?.toUpperCase() === 'NL' && locale.toLowerCase() !== 'de-de';
  return isNlWithNonGermanLocale ? 'en-NL' : 'en-US';
};

/**
 * Market locales that have no Contentful locale of their own, mapped to the labels they use.
 * English in France and Germany reads the en-US labels.
 */
export const LABELS_LOCALE_FALLBACKS: Readonly<Record<string, string>> = {
  'en-FR': 'en-US',
  'en-DE': 'en-US',
};

/**
 * Maps a Contentful locale to the one labels are actually fetched in.
 * Locales without their own Contentful locale (en-FR, en-DE) use their fallback,
 * every other locale is returned unchanged.
 * @param locale - Locale in Contentful format like 'en-DE', 'fr-FR'
 * @returns Locale to request labels in
 * @example
 * ```ts
 * resolveLabelsLocale('en-DE') // 'en-US'
 * resolveLabelsLocale('fr-FR') // 'fr-FR'
 * ```
 */
export const resolveLabelsLocale = (locale: string): string =>
  LABELS_LOCALE_FALLBACKS[locale] ?? locale;

/**
 * Formats locale for API calls by converting to underscore format
 * Adds '_us' suffix for 2-character locales
 * @param locale - Locale in format like 'en', 'en-us', 'nl-nl'
 * @returns Locale in API format like 'en_us', 'en_us', 'nl_nl'
 * @example
 * ```ts
 * formatLocaleForApi('en') // 'en_us'
 * formatLocaleForApi('en-us') // 'en_us'
 * formatLocaleForApi('nl-nl') // 'nl_nl'
 * ```
 */
export const formatLocaleForApi = (locale: string): string => {
  if (!locale) return 'de_de';
  if (locale.length === 2) return `${locale}_us`;
  return locale.replace('-', '_');
};

/**
 * Extract ISO 3166-1 alpha-2 country code from locale
 * @param locale - Locale string (e.g., 'en-us', 'de-de', 'nl-nl')
 * @returns ISO 3166-1 alpha-2 country code (e.g., 'US', 'DE', 'NL')
 * @example
 * getCountryCodeFromLocale('en-us') // 'US'
 * getCountryCodeFromLocale('de-de') // 'DE'
 * getCountryCodeFromLocale('en') // 'US'
 */
export const getCountryCodeFromLocale = (locale: string): string => {
  if (!locale) return 'DE';
  const normalizedLocale = locale.toLowerCase();

  // Handle special cases
  if (normalizedLocale === 'en' || normalizedLocale === 'en-us') return 'US';

  // Extract country code from locale format (e.g., 'de-de' -> 'DE')
  const parts = normalizedLocale.split('-');
  if (parts.length === 2) {
    return parts[1].toUpperCase();
  }

  // Fallback to DE (default market) if cannot determine
  return 'DE';
};

/*
|--------------------------------------------------------------------------
| LOCAL STORAGE HELPERS
|--------------------------------------------------------------------------
*/

/**
 * Get an item from localStorage with JSON parsing
 * @param key - The storage key
 * @returns The parsed value or null if not found/invalid
 * @example
 * ```ts
 * getStorageItem<UserIdentity>('user-identity')
 * ```
 */
export const getStorageItem = <T>(key: string): T | null => {
  try {
    const item = localStorage.getItem(key);
    if (item) {
      return JSON.parse(item) as T;
    }
  } catch {
    // Ignore parse errors
  }
  return null;
};

/**
 * Set an item in localStorage with JSON stringification
 * @param key - The storage key
 * @param value - The value to store
 * @example
 * ```ts
 * setStorageItem('user-identity', userIdentity)
 * ```
 */
export const setStorageItem = <T>(key: string, value: T): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage errors
  }
};

/**
 * Remove an item from localStorage
 * @param key - The storage key
 * @example
 * ```ts
 * removeStorageItem('user-identity')
 * ```
 */
export const removeStorageItem = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore storage errors
  }
};

/**
 * Formats a string by replacing hyphens and underscores with spaces
 * and capitalizing the first letter of each word (Title Case)
 * @param str - The string to format
 * @returns Formatted string in Title Case
 * @example
 * ```ts
 * formatString('destination_charge') // "Destination Charge"
 * formatString('add-ons') // "Add Ons"
 * formatString('total price') // "Total Price"
 * formatString('hello') // "Hello"
 * ```
 */
export const formatString = (str: string | null | undefined): string => {
  if (!str) return '';
  return str
    .replace(/[-_]/g, ' ') // Replace hyphens and underscores with spaces
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Get the currency code for a given locale
 * @param locale - The locale string (e.g., 'en-us', 'de-de')
 * @returns ISO 4217 currency code
 */
export const getCurrencyCodeForLocale = (locale: string): string => {
  if (!locale) return 'EUR';
  const normalizedLocale = locale.toLowerCase();
  switch (normalizedLocale) {
    case 'en':
    case 'en-us':
      return 'USD';
    case 'en-ca':
    case 'fr-ca':
      return 'CAD';
    case 'de-de':
    case 'nl-nl':
    case 'en-be':
    case 'fr-be':
    case 'nl-be':
    case 'de-be':
      return 'EUR';
    case 'nb-no':
      return 'NOK';
    case 'da-dk':
      return 'DKK';
    case 'ar-sa':
    case 'en-sa':
      return 'SAR';
    case 'en-ae':
    case 'ar-ae':
      return 'AED';
    case 'fr-ch':
    case 'de-ch':
      return 'CHF';
    default:
      return 'EUR';
  }
};

/**
 * Get currency symbol for a currency code
 * @param currencyCode - ISO 4217 currency code
 * @returns Currency symbol
 */
export const getCurrencySymbol = (currencyCode: string): string => {
  const symbols: Record<string, string> = {
    USD: '$',
    CAD: '$',
    EUR: '€',
    NOK: 'kr',
    DKK: 'kr',
    SAR: 'ر.س',
    AED: 'د.إ',
    CHF: 'CHF',
  };
  return symbols[currencyCode] || currencyCode;
};

type LocalePriceValue = string | number | undefined | null;

/**
 * Parses a numeric string that may be locale-formatted into a JavaScript number.
 * Handles both raw dot-decimal strings ("5488.6") and locale-formatted strings
 * ("5.488,6" for de-DE or "5,488.6" for en-US).
 * @param value - A numeric string, potentially locale-formatted
 * @returns The parsed number, or 0 if the value is empty/unparseable
 */
export const parseLocalePrice = (value: LocalePriceValue): number => {
  if (!value) return 0;

  if (typeof value === 'number') return value;

  const direct = Number(value);
  if (!Number.isNaN(direct)) return direct;

  // Strip everything except digits, dots, commas, and leading minus
  const cleaned = value.replace(/[^\d.,-]/g, '');
  if (!cleaned) return 0;

  const lastDot = cleaned.lastIndexOf('.');
  const lastComma = cleaned.lastIndexOf(',');

  if (lastComma > lastDot) {
    // Comma is the decimal separator (e.g., "5.488,6" -> de-DE)
    return parseFloat(cleaned.replace(/\./g, '').replace(',', '.')) || 0;
  }

  // Dot is the decimal separator (e.g., "5,488.6") or no separator at all
  return parseFloat(cleaned.replace(/,/g, '')) || 0;
};

/*
|--------------------------------------------------------------------------
| LOGGING UTILITIES
|--------------------------------------------------------------------------
*/

/**
 * Development-only logger that only logs in non-production environments
 * Prevents accidental logging of sensitive data in production
 * @param message - Message to log
 * @param data - Optional data to log
 * @example
 * ```ts
 * devLog('User authenticated', { userId: user.id });
 * ```
 */
export const devLog = (message: string, data?: unknown): void => {
  if (import.meta.env.DEV) {
    if (data !== undefined) {
      console.log(`[DEV] ${message}`, data);
    } else {
      console.log(`[DEV] ${message}`);
    }
  }
};

/**
 * Development-only warning logger
 * @param message - Warning message
 * @param data - Optional data to log
 */
export const devWarn = (message: string, data?: unknown): void => {
  if (import.meta.env.DEV) {
    if (data !== undefined) {
      console.warn(`[DEV] ${message}`, data);
    } else {
      console.warn(`[DEV] ${message}`);
    }
  }
};

/*
|--------------------------------------------------------------------------
| FILE DOWNLOAD HELPERS
|--------------------------------------------------------------------------
*/

/**
 * Triggers a file download from a URL
 * Creates a temporary anchor element and clicks it to initiate the download.
 * @param url - The URL to download from (can be a blob URL, data URL, or remote URL)
 * @param filename - The filename to use for the download
 * @example
 * ```ts
 * // Download from a presigned URL
 * triggerLinkDownload('https://storage.example.com/file?token=abc', 'document.pdf');
 *
 * // Download from a blob URL
 * const blobUrl = URL.createObjectURL(blob);
 * triggerLinkDownload(blobUrl, 'export.csv');
 * ```
 */
export const triggerLinkDownload = (url: string, filename: string): void => {
  if (!url || !filename) return;

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
};

/**
 * Triggers a file download from a Blob
 * @param blob - The Blob to download
 * @param filename - The filename to use for the download
 * @example
 * ```ts
 * const blob = new Blob([data], { type: 'application/pdf' });
 * triggerBlobDownload(blob, 'invoice.pdf');
 * ```
 */
export const triggerBlobDownload = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  triggerLinkDownload(url, filename);
  window.URL.revokeObjectURL(url);
};

/*
|--------------------------------------------------------------------------
| SECURITY HELPERS
|--------------------------------------------------------------------------
*/

/**
 * Escape HTML special characters to prevent XSS when rendering user-supplied
 * strings via dangerouslySetInnerHTML (e.g. in Typography components).
 * @param str - The raw string from an API response
 * @returns HTML-escaped string safe for injection into the DOM
 */
export const escapeHtml = (str: string): string =>
  str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/*
|--------------------------------------------------------------------------
| VEHICLE HELPERS
|--------------------------------------------------------------------------
*/

/**
 * Normalizes a vehicle model name to a consistent, brand-prefixed display value.
 * Backend payloads return the model inconsistently (e.g. "air", "Air",
 * "Air Grand Touring", "lucid-air", "Lucid Air"); this produces a single
 * canonical form such as "Lucid Air" or "Lucid Air Grand Touring".
 * @param model - The raw model string from an API payload
 * @returns Consistent display name, or '-' when no model is provided
 * @example
 * ```ts
 * formatVehicleModelName('air') // "Lucid Air"
 * formatVehicleModelName('Air Grand Touring') // "Lucid Air Grand Touring"
 * formatVehicleModelName('lucid-gravity') // "Lucid Gravity"
 * formatVehicleModelName(null) // "-"
 * ```
 */
export const formatVehicleModelName = (model: string | null | undefined): string => {
  if (!model || !model.trim()) return '-';

  const titleCased = formatString(model).replace(/\s+/g, ' ').trim();

  if (/^lucid\b/i.test(titleCased)) return titleCased;
  if (/^(air|gravity)\b/i.test(titleCased)) return `Lucid ${titleCased}`;

  return titleCased;
};

/**
 * Removes optional brand and model prefixes from a vehicle trim.
 * @example normalizeVehicleTrim('Lucid Gravity Touring Plus', 'gravity') // 'Touring Plus'
 */
export const normalizeVehicleTrim = (trim: string, model: string): string => {
  const trimName = trim
    .trim()
    .replace(/^Lucid\s+/i, '')
    .replace(/\s+/g, ' ');
  const modelName = model
    .trim()
    .replace(/^Lucid\s+/i, '')
    .replace(/\s+/g, ' ');
  const prefix = `${modelName} `;
  return modelName && trimName.toLowerCase().startsWith(prefix.toLowerCase())
    ? trimName.slice(prefix.length).trim()
    : trimName;
};

/*
|--------------------------------------------------------------------------
| ADDRESS HELPERS
|--------------------------------------------------------------------------
*/

// Dutch postcodes are 4 digits and 2 letters, e.g. 1234 AB.
const POSTAL_CODE_PATTERNS: Record<string, RegExp> = {
  NL: /^\d{4}\s?[a-z]{2}$/i,
};

export const getPostalCodePattern = (countryCode?: string | null): RegExp | undefined =>
  countryCode ? POSTAL_CODE_PATTERNS[countryCode.toUpperCase()] : undefined;
